import React from "react";
import { createRoot } from "react-dom/client";

import {
  LazyFragmentData,
  RecommendationData,
  RecommendationPayload,
  RecommendationRequest
} from "../types/fragment";
import {
  BasketPageLoadedEvent,
  ProductRecommendationRenderedEvent
} from "../types/app-shell-events";
import { CustomerType } from "../types/shop-cookies";
import { fetchRecommendations } from "./fetch-recommendations";
import { getCustomerType } from "./customer-type";
import { initConfigIdObserver } from "./config-id-observer";
import { requestCookieConsent } from "./consent-cookie";
import { RecommendationArticlesProps } from "../types/articles";
import { StartReloader } from "./reloader";
import RecommendationArticles from "../components/articles-container/recommendation-articles";

export function StartLazyLoader(data: RecommendationData, prsScopes: NodeListOf<Element>) {
  const isBasketPage = document.querySelector(".basket-page-container") !== null;
  const isPrivateCustomer = getCustomerType() === CustomerType.Private;
  let allowAiRecommendations = false;
  let basketEvent: BasketPageLoadedEvent | undefined;

  requestCookieConsent()
    .then((consent) => (allowAiRecommendations = consent.AIRecommendations ?? false))
    .catch((reason) => console.debug("request for CookieConsent failed", reason));

  const loadConfigById = (scope: Element, useDefaultId: boolean) => {
    const location = scope.getAttribute("data-location")!;
    const defaultConfigId = scope.getAttribute("data-default-config-id")!;
    const customConfigId = scope.getAttribute("data-config-id");

    const fragmentData: LazyFragmentData = {
      container: scope,
      apiUrl: data.apiUrl,
      l10n: data.l10n,
      location: location,
      defaultConfigId: defaultConfigId,
      customConfigId: customConfigId,
      useDefaultId: useDefaultId
    };

    const recoReqData: RecommendationRequest = {
      allowAiRecommendations: allowAiRecommendations,
      isPrivateCustomer: isPrivateCustomer,
      salesArticleNumbersToIgnore: basketEvent?.classicArticlesWithoutPersonalization?.map(
        (a) => a.salesArticleNumber
      ),
      freeShippingThresholdNet: basketEvent?.shippingCosts.freeShippingThresholdNet,
      basketTotalValueNet: basketEvent?.costs.basketTotalValueNet
    };

    console.debug("reco req data", recoReqData);
    fetchRecommendations(fragmentData, recoReqData, true);
  };

  const initObserver = (source: string) => {
    console.debug("init observer for:", source);
    initConfigIdObserver(
      [...prsScopes],
      (element) => loadConfigById(element, false),
      (remaining) => {
        remaining.forEach((element) => loadConfigById(element, true));
      }
    );
  };

  // if PRS is loaded on the BasketPage it will wait for the BasketPageLoaded event and then start the observer
  if (isBasketPage) {
    let eventReceived = false;

    shell?.messageBroker.subscribeTo(
      "ESSA.BasketPage.BasketPageLoaded",
      (payload) => {
        if (payload) {
          eventReceived = true;
          basketEvent = payload as BasketPageLoadedEvent;
          initObserver("BasketPageLoaded event");
        }
      },
      "ESPP2.ProductRecommendation"
    );

    setTimeout(() => {
      if (!eventReceived) {
        initObserver("using fallback without BasketPageLoaded event");
      }
    }, 3000);
  } else {
    initObserver("not a basket page");
  }
}

export function initRecommendationRoot(
  payload: RecommendationPayload,
  fragmentData: LazyFragmentData,
  recoReqData: RecommendationRequest,
  isInitialLoad: boolean
) {
  const props: RecommendationArticlesProps = {
    recommendations: payload.recommendations,
    currencyInformation: payload.currencyInformation,
    l10n: fragmentData.l10n,
    location: fragmentData.location,
    defaultConfigId: fragmentData.defaultConfigId,
    useDefaultId: fragmentData.useDefaultId,
    customConfigId: fragmentData.customConfigId
  };

  const rootRecommendation = createRoot(fragmentData.container);
  rootRecommendation.render(<RecommendationArticles {...props} />);
  StartReloader(rootRecommendation, fragmentData, recoReqData);

  const eventData: ProductRecommendationRenderedEvent = {
    isInitialRender: isInitialLoad,
    fragmentName: fragmentData.location,
    requestConfigId: fragmentData.useDefaultId
      ? fragmentData.defaultConfigId
      : fragmentData.customConfigId!
  };

  shell?.messageBroker.publishTo("ESPP2.ProductRecommendation.Rendered", eventData);
}
